import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getBaseUrl } from "../../config/apiConfig";
import { apiService } from "../../backend/api";
import {
  Shield,
  ArrowLeft,
  Save,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  User,
  ShieldCheck
} from "lucide-react";

function AdminAssignRoles() {
  const { state } = useLocation();
  const params = useParams();
  const userId = params.userId || state?.userId;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const fetchRoles = useCallback(async () => {
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/roles`);
      if (data && Array.isArray(data)) {
        setRoles(data);
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der Rollen:", error);
      setError("Rollen konnten nicht geladen werden.");
    }
  }, []);

  const fetchUserRoles = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/roles/${userId}`);
      if (data) {
        setSelectedRoles(data.map(role => role.name));
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der Benutzerrollen:", error);
      setError("Benutzerrollen konnten nicht geladen werden.");
    }
  }, [userId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      await Promise.all([fetchRoles(), fetchUserRoles()]);
      setLoading(false);
    };
    loadData();
  }, [fetchRoles, fetchUserRoles]);

  const toggleRole = (roleName) => {
    setSelectedRoles((prev) =>
      prev.includes(roleName)
        ? prev.filter((r) => r !== roleName)
        : [...prev, roleName]
    );
  };

  const submitRoles = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(false);
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/assign-roles`, {
        method: "POST",
        body: JSON.stringify({
          userId,
          roles: selectedRoles,
        }),
      });

      if (data && data.status === "success") {
        setSuccess(true);
        setTimeout(() => {
          navigate("/admin/dashboard");
        }, 1500);
      }
    } catch (error) {
      console.error("Fehler beim Zuweisen der Rollen:", error);
      setError(t("adminAssignRoles.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <RefreshCw className="text-[#0f7699] dark:text-[#4fc3f7] animate-spin mb-4" size={48} />
        <p className="text-slate-500 font-semibold">{t("adminAssignRoles.loadingRoles")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans text-slate-900 pb-12">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center space-x-2 text-slate-500 hover:text-[#0f7699] dark:text-[#4fc3f7] transition-colors font-semibold py-2 px-3 hover:bg-slate-100 rounded-xl"
          >
            <ArrowLeft size={20} />
            <span>Zurück zur Übersicht</span>
          </button>

          <div className="flex items-center space-x-2 text-[#0f7699] dark:text-[#4fc3f7]">
            <ShieldCheck size={24} />
            <span className="text-xl font-bold tracking-tight">Rollenverwaltung</span>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 p-8 lg:p-12 border border-slate-100 animate-in fade-in slide-in-from-bottom-4 duration-500">

          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 text-[#0f7699] dark:text-[#4fc3f7] rounded-3xl flex items-center justify-center mx-auto mb-6">
              <User size={40} strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-extrabold text-slate-800 mb-2">Berechtigungen anpassen</h1>
            <p className="text-slate-500">Weisen Sie dem Benutzer die entsprechenden Systemrollen zu.</p>
            {userId && (
              <p className="mt-2 text-xs font-mono text-slate-400">User ID: {userId}</p>
            )}
          </div>

          {error && (
            <div className="mb-8 p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-r-xl flex items-center space-x-3">
              <AlertCircle size={20} className="shrink-0" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-8 p-4 bg-green-50 border-l-4 border-green-500 text-green-800 rounded-r-xl flex items-center space-x-3">
              <CheckCircle2 size={20} className="shrink-0" />
              <p className="text-sm font-medium">Rollen erfolgreich zugewiesen! Leite weiter...</p>
            </div>
          )}

          <div className="space-y-4 mb-12">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Verfügbare Rollen</h3>
            {roles.length === 0 ? (
              <div className="text-center py-8 text-slate-400 italic">
                Keine Rollen im System gefunden.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {roles.map((role) => {
                  const isSelected = selectedRoles.includes(role.name);
                  return (
                    <button
                      key={role._id}
                      onClick={() => toggleRole(role.name)}
                      className={`flex items-center justify-between p-5 rounded-2xl border-2 transition-all duration-200 text-left outline-none ${
                        isSelected
                          ? "border-[#0f7699] dark:border-[#4fc3f7] bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10/50 ring-4 ring-[#0f7699]/10 dark:ring-[#4fc3f7]/10"
                          : "border-slate-100 bg-slate-50 hover:border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          isSelected ? "bg-[#0f7699] dark:bg-[#4fc3f7] text-white" : "bg-slate-200 text-slate-400"
                        }`}>
                          <Shield size={20} />
                        </div>
                        <div>
                          <p className={`font-bold ${isSelected ? "text-[#0f7699] dark:text-[#4fc3f7]" : "text-slate-700"}`}>
                            {role.name}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5 uppercase tracking-tighter">System Rolle</p>
                        </div>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/100 border-[#0f7699] dark:border-[#4fc3f7] text-white" : "border-slate-300 bg-white"
                      }`}>
                        {isSelected && <CheckCircle2 size={14} className="fill-current" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            onClick={submitRoles}
            disabled={submitting || success}
            className="w-full h-16 bg-[#0f7699] dark:bg-[#4fc3f7] hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 disabled:bg-slate-200 dark:disabled:bg-slate-700 text-white dark:text-slate-900 rounded-2xl font-bold shadow-lg shadow-[#0f7699]/20 dark:shadow-[#4fc3f7]/20 transition-all flex items-center justify-center space-x-3 group active:scale-[0.98]"
          >
            {submitting ? (
              <RefreshCw className="animate-spin" size={20} />
            ) : (
              <>
                <Save size={20} />
                <span>Änderungen speichern</span>
              </>
            )}
          </button>
        </div>
      </main>
    </div>
  );
}

export default AdminAssignRoles;

